import { ApiState, Paginate } from '@/store/general.types';
import {
    ScreeningOverviewDTO,
    ScreeningSortOrderItem,
    ScreeningSortBy,
} from '@/views/Screening/types/screening-view.types';
import { Study } from '../study/study.types';

export interface ScreeningState {
    getScreeningRecordsCount: ApiState<ScreeningRecordsCountV2>;
    getScreeningOverviewCount: ApiState<ScreeningOverviewDTO>;
    getStudyScreeningRecordsCount: ApiState<StudyScreeningRecordsCount>;
    getAwaitingScreeningRecords: ApiState<Paginate<Study>>;
    editCitationRecord: ApiState<EditCitationDTO>;
    getStudiesByScreeningTypeStatus: ApiState<Paginate<Study>>;
    getMyDecisionScreeningRecords: ApiState<Paginate<Study>>;
    updateStudyScreeningStatus: ApiState<Study>;
    revertStudyScreeningDecision: ApiState<void>;
    revertMyDecisionScreeningRecords: ApiState<void>;
    getConflictedStudyScreeningRecords: ApiState<Paginate<Study>>;
    getResolvedScreeningRecords: ApiState<Paginate<Study>>;
    revertResolved: ApiState<RevertDecisionDTO>;
    resolveConflict: ApiState<Study>;
    mergeStudies: ApiState<void>;
    unmergedStudies: ApiState<void>;
    getHighlightedKeywords: ApiState<HighlightedKeywordsDTO>;
    showAbstract: boolean;
    isKeywordHighlighted: boolean;
}

export interface ScreeningRecordsCount {
    totalImportedCount: number;
    removedDuplicateCount: number;
    possibleDuplicateCount: number;
    awaitScreeningCount: number;
    failedImportCount: number;
    // JIRA: SUMDEV-2964 - Added breakdown for removed duplicates
    removedDuplicatesBreakdown?: {
        systemDetected: number;
        manuallyMarked: number;
    };
}

/**
 * JIRA: SUMDEV-2964
 * New cluster-based screening counts with breakdown
 */
export interface ScreeningRecordsCountV2 {
    totalImportedRecords: number;
    removedDuplicates: {
        systemDetected: number;
        manuallyMarked: number;
        total: number;
    };
    possibleDuplicates: number;
}

export interface StudyScreeningRecordsCount {
    awaitingScreeningCount: number;
    includedScreeningCount: number;
    excludedScreeningCount: number;
    resolvedScreeningCount: number;
    conflictedScreeningCount: number;
    userDecisionScreeningCount: number;
}

export interface StudyScreeningRecord {
    id: number;
    projectId: number;
    studyId: number;
    screeningStage: StudyScreeningType;
    screeningStatus: StudyScreeningStatus;
    updatedDate: number;
    reasonExcluded: string;
    userId: number;
    userFullName: string;
    conflicted: boolean;
    revertedEarlier: boolean;
    screenedManually: boolean;
    previousScreeningStatus: StudyScreeningStatus;
    previousReasonExcluded: string;
    isUploader: boolean;
    isConflictResolver: boolean;
}

export enum ConflictResolutionStatus {
    NA = 'NA',
    PENDING = 'PENDING',
    RESOLVED_BY_INCLUSION = 'RESOLVED_BY_INCLUSION',
    RESOLVED_BY_EXCLUSION = 'RESOLVED_BY_EXCLUSION',
}

export interface GetStudiesByScreeningTypeStatusPayload {
    projectId: number;
    screeningType: StudyScreeningType;
    screeningStatus: StudyScreenedStatus;
    page: number;
    perPage: number;
    search: string;
    sortOrder: ScreeningSortOrderItem;
    sortBy?: ScreeningSortBy;
}

export interface GetStudiesByUserIdPayload {
    projectId: number;
    userId: number;
    screeningType: StudyScreeningType;
    screeningStatus: StudyScreeningStatus;
    page: number;
    perPage: number;
    search: string;
    sortOrder: ScreeningSortOrderItem;
    sortBy?: ScreeningSortBy;
}

export interface UpdateStudyScreeningStatusPayload {
    projectId: number;
    studyId: number;
    screeningType: StudyScreeningType;
    screeningAction: StudyScreenedStatus;
    reasonForExclusion?: string;
}

export interface RevertStudyScreeningDecisionPayload {
    projectId: number;
    screeningType: StudyScreeningType;
    studyId: number;
}

export enum StudyScreeningType {
    TITLE_ABSTRACT = 'TITLE_ABSTRACT',
    FULL_TEXT = 'FULL_TEXT',
}

export interface RevertDecisionPayload {
    projectId: number;
    studyId: number;
    conflictResolution?: StudyScreenedStatus;
    conflictResolutionComment?: string;
    screeningType: StudyScreeningType;
    reasonExcluded?: string;
}
export interface RevertDecisionDTO {
    studyId: number;
    projectId: number;
    message: string;
}

export interface HighlightedKeyword {
    id: number;
    highlightedKeyword: string;
    color: {
        colorName: string;
        colorCode: string;
    };
}

export interface HighlightedKeywordsDTO {
    projectId: number;
    data: Array<HighlightedKeyword>;
}

export interface ResolveConflictPayload {
    projectId: number;
    studyId: number;
    screeningType: StudyScreeningType;
    conflictResolution: StudyScreeningStatus;
    conflictResolutionComment?: string;
    reasonExcluded?: string;
}

export interface ResolveConflictDTO {
    studyId: number;
    projectId: number;
    message: string;
}

export enum StudyScreeningStatus {
    PENDING = 'PENDING',
    INCLUDED = 'INCLUDED',
    EXCLUDED = 'EXCLUDED',
    CONFLICTED = 'CONFLICTED',
    DISABLED = 'DISABLED',
    ALL = 'ALL',
}

export interface EditCitationDTO {
    id: number;
    type?: string;
    containerTitle?: string;
    volume?: string;
    issue?: string;
    abstract?: string;
    title?: string;
    author?: any;
    publicationYear?: string;
    DOI?: string;
    url?: string;
    primaryAuthor?: string;
    page?: string;
    publisher?: string;
    publisherPlace?: string;
    edition?: string;
    collectionEditor?: string;
    editor?: string;
    event?: string;
    eventPlace?: string;
    number?: string;
    numberOfPages?: string;
    note?: string;
    accessedDate?: string;
}

export interface StudyScreeningRecordsItems {
    items: StudyScreeningRecord[];
    totalCount: number;
}

export interface ScreeningRecordPayLoad {
    projectId: number;
    userId: number;
    screeningType: StudyScreeningType;
    screeningStatus: StudyScreeningStatus;
    page: number;
    perPage: number;
    search: string;
    url: boolean;
    sortOrder?: ScreeningSortOrderItem;
    sortBy?: ScreeningSortBy;
}

export interface StudyScreeningRecordsCountPayLoad {
    projectId: number;
    userId: number;
    screeningType: StudyScreeningType;
    search: string;
}

export interface ScreeningOverviewPayLoad {
    projectId: number;
    userId: number;
}

export type StudyScreenedStatus =
    | StudyScreeningStatus.INCLUDED
    | StudyScreeningStatus.EXCLUDED;

export interface MergeStudiesPayload {
    projectId: number;
    primaryStudyId: number;
    studyIds: number[];
}

export enum ExportFileFormat {
    CSV = 'CSV',
    DOCX = 'DOCX',
    RIS = 'RIS',
    XML = 'XML',
}
